import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ClothList.module.css';
import {
  fetchClothListByStore,
  updateClothItem,
  findClothById,
} from '../../Api/clothApi.js';

/* ------------------------------------------------------------------ *
 *  Domain constants
 * ------------------------------------------------------------------ */

const CLOTH_TYPES = ['SHIRTING', 'SUITING'];

// Shirting panna is a physical width; suiting offers SUITING or SALWAR.
const SHIRTING_PANNA_OPTIONS = ['36"', '58"'];
const SUITING_PANNA = 'SUITING';
const SUITING_SALWAR = 'SALWAR';
const SUITING_PANNA_OPTIONS = [SUITING_PANNA, SUITING_SALWAR];
const DEFAULT_SHIRTING_PANNA = '58"';

// Panna values that only make sense for suiting cloth.
const SUITING_PANNA_SET = new Set(SUITING_PANNA_OPTIONS);

// Fields the user can actually change, in table order. Barcode/description are read-only.
const EDITABLE_FIELDS = [
  'itemCategory',
  'type',
  'pannaType',
  'price',
  'wholeSalePrice',
];

const FIELD_LABELS = {
  itemCategory: 'Item Category',
  type: 'Cloth Type',
  pannaType: 'Panna Type',
  price: 'Retail Price',
  wholeSalePrice: 'Wholesale Price',
};

const PRICE_FIELDS = new Set(['price', 'wholeSalePrice']);

// Columns exported / imported, matching the backend ItemCloth shape.
const EXPORT_COLUMNS = [
  'itemBarcodeID',
  'itemCategory',
  'type',
  'pannaType',
  'price',
  'wholeSalePrice',
];

/* ------------------------------------------------------------------ *
 *  Pure helpers (kept outside the component so they never re-create)
 * ------------------------------------------------------------------ */

const getStoreId = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    return user?.storeId || '';
  } catch {
    return '';
  }
};

// Service responses come back either as a bare array or wrapped in { status, payload }.
const unwrap = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.payload)) return response.payload;
  return [];
};

const succeeded = (response) =>
  Array.isArray(response) ||
  String(response?.status).toLowerCase() === 'success';

// Panna width is a shirting concept. For suiting we keep SUITING/SALWAR (preserving
// a prior SALWAR pick), and for shirting we restore a sane physical width default.
const resolvePannaType = (clothType, currentPanna) => {
  if (clothType === 'SUITING') {
    return currentPanna === SUITING_SALWAR ? SUITING_SALWAR : SUITING_PANNA;
  }
  // Switching to shirting: drop any suiting-only panna and default to a width.
  if (SUITING_PANNA_SET.has(currentPanna) || !currentPanna) {
    return DEFAULT_SHIRTING_PANNA;
  }
  return currentPanna;
};

const pannaOptionsFor = (clothType) =>
  clothType === 'SUITING' ? SUITING_PANNA_OPTIONS : SHIRTING_PANNA_OPTIONS;

// Human-readable label for a panna value.
const pannaLabel = (panna) => {
  if (panna === SUITING_PANNA) return 'Suiting';
  if (panna === SUITING_SALWAR) return 'Salwar';
  return panna;
};

// Compare edited value against the original. Prices are compared numerically so
// "500" and 500 don't register as a change; everything else compares as trimmed text.
const valuesDiffer = (field, originalValue, editedValue) => {
  if (PRICE_FIELDS.has(field)) {
    return Number(originalValue ?? '') !== Number(editedValue ?? '');
  }
  return String(originalValue ?? '') !== String(editedValue ?? '');
};

// The editable slice of a record, normalised to strings for controlled inputs.
const toDraft = (row) => ({
  itemCategory: row.itemCategory ?? '',
  type: row.type ?? 'SHIRTING',
  pannaType: row.pannaType ?? '',
  price: row.price ?? '',
  wholeSalePrice: row.wholeSalePrice ?? '',
});

// Which editable fields differ from the original record.
const diffFields = (original, draft) =>
  EDITABLE_FIELDS.filter((field) =>
    valuesDiffer(field, original[field], draft[field])
  );

// The API wants only changed fields, each as { key, value } strings.
const buildUpdateRequests = (original, draft) =>
  diffFields(original, draft).map((field) => ({
    key: field,
    value: String(draft[field]),
  }));

// Per-cell validation. Returns a message for the first broken rule, else null.
const validateCell = (field, draft) => {
  if (!PRICE_FIELDS.has(field)) return null;

  const raw = draft[field];
  if (raw === '' || raw === null || raw === undefined) {
    return `${FIELD_LABELS[field]} is required.`;
  }

  const value = Number(raw);
  if (Number.isNaN(value)) return `${FIELD_LABELS[field]} must be a number.`;
  if (value < 0) return `${FIELD_LABELS[field]} cannot be negative.`;

  if (field === 'wholeSalePrice') {
    const retail = Number(draft.price);
    if (!Number.isNaN(retail) && value > retail) {
      return 'Wholesale price cannot exceed retail price.';
    }
  }
  return null;
};

// All broken rules for a draft, keyed by field. Empty object == valid row.
const validateDraft = (draft) => {
  const errors = {};
  for (const field of EDITABLE_FIELDS) {
    const message = validateCell(field, draft);
    if (message) errors[field] = message;
  }
  return errors;
};

/* ------------------------------------------------------------------ *
 *  CSV helpers — small, dependency-free
 * ------------------------------------------------------------------ */

// Quote a value if it contains a comma, quote, or newline.
const csvCell = (value) => {
  const text = String(value ?? '');
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const rowsToCsv = (rows) => {
  const header = EXPORT_COLUMNS.join(',');
  const body = rows.map((row) =>
    EXPORT_COLUMNS.map((col) => csvCell(row[col])).join(',')
  );
  return [header, ...body].join('\n');
};

// Parse a CSV string into an array of objects keyed by header. Handles quoted
// fields with embedded commas / quotes / newlines.
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  // Flush trailing cell/row (files without a final newline).
  if (cell !== '' || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }

  if (rows.length === 0) return [];

  const headers = rows[0].map((h) => h.trim());
  return rows
    .slice(1)
    .filter((cells) => cells.some((c) => String(c).trim() !== ''))
    .map((cells) => {
      const obj = {};
      headers.forEach((key, idx) => {
        obj[key] = cells[idx] !== undefined ? cells[idx].trim() : '';
      });
      return obj;
    });
};

// Normalise an imported record's editable slice, tolerating loose type/panna casing.
const normaliseImported = (record) => {
  const rawType = String(record.type ?? '').toUpperCase();
  const type = CLOTH_TYPES.includes(rawType) ? rawType : 'SHIRTING';

  const upperPanna = String(record.pannaType ?? '').toUpperCase();
  let pannaType;
  if (upperPanna === SUITING_PANNA) {
    pannaType = SUITING_PANNA;
  } else if (upperPanna === SUITING_SALWAR) {
    pannaType = SUITING_SALWAR;
  } else {
    pannaType = String(record.pannaType ?? '');
  }
  pannaType = resolvePannaType(type, pannaType);

  return {
    itemCategory: record.itemCategory ?? '',
    type,
    pannaType,
    price: record.price ?? '',
    wholeSalePrice: record.wholeSalePrice ?? '',
  };
};

/* ------------------------------------------------------------------ *
 *  Status badge
 * ------------------------------------------------------------------ */

const STATUS_META = {
  clean: { label: 'No changes', className: 'statusClean' },
  modified: { label: 'Modified', className: 'statusModified' },
  saving: { label: 'Saving…', className: 'statusSaving' },
  saved: { label: 'Saved', className: 'statusSaved' },
  failed: { label: 'Failed', className: 'statusFailed' },
};

const StatusBadge = React.memo(function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.clean;
  return <span className={`${styles.badge} ${styles[meta.className]}`}>{meta.label}</span>;
});

/* ------------------------------------------------------------------ *
 *  Editable table row
 * ------------------------------------------------------------------ */

const ClothRow = React.memo(function ClothRow({
  index,
  original,
  draft,
  changedFields,
  errors,
  status,
  onChange,
  onUndo,
  saving,
}) {
  const isModified = changedFields.length > 0;
  const changed = useMemo(() => new Set(changedFields), [changedFields]);

  const cellClass = (field) => {
    const classes = [styles.cell];
    if (errors[field]) classes.push(styles.cellInvalid);
    else if (changed.has(field)) classes.push(styles.cellChanged);
    return classes.join(' ');
  };

  const rowClass = [
    styles.row,
    isModified ? styles.rowModified : '',
    status === 'failed' ? styles.rowFailed : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <tr className={rowClass}>
      <td className={styles.barcodeCell} title={original.itemBarcodeID}>
        {original.itemBarcodeID || '—'}
      </td>

      <td className={cellClass('itemCategory')}>
        <input
          className={styles.input}
          type="text"
          value={draft.itemCategory}
          disabled={saving}
          onChange={(e) => onChange(original.sno, 'itemCategory', e.target.value)}
          placeholder="Category"
        />
      </td>

      <td className={cellClass('type')}>
        <select
          className={styles.input}
          value={draft.type}
          disabled={saving}
          onChange={(e) => onChange(original.sno, 'type', e.target.value)}
        >
          {CLOTH_TYPES.map((type) => (
            <option key={type} value={type}>
              {type === 'SUITING' ? 'Suiting' : 'Shirting'}
            </option>
          ))}
        </select>
      </td>

      <td className={cellClass('pannaType')}>
        <select
          className={styles.input}
          value={draft.pannaType}
          disabled={saving}
          onChange={(e) => onChange(original.sno, 'pannaType', e.target.value)}
        >
          {pannaOptionsFor(draft.type).map((panna) => (
            <option key={panna} value={panna}>
              {pannaLabel(panna)}
            </option>
          ))}
        </select>
      </td>

      <td className={cellClass('price')}>
        <input
          className={styles.input}
          type="number"
          min="0"
          value={draft.price}
          disabled={saving}
          onChange={(e) => onChange(original.sno, 'price', e.target.value)}
        />
        {errors.price && <span className={styles.cellError}>{errors.price}</span>}
      </td>

      <td className={cellClass('wholeSalePrice')}>
        <input
          className={styles.input}
          type="number"
          min="0"
          value={draft.wholeSalePrice}
          disabled={saving}
          onChange={(e) => onChange(original.sno, 'wholeSalePrice', e.target.value)}
        />
        {errors.wholeSalePrice && (
          <span className={styles.cellError}>{errors.wholeSalePrice}</span>
        )}
      </td>

      <td className={styles.descriptionCell} title={original.description}>
        {original.description || '—'}
      </td>

      <td className={styles.statusCell}>
        <StatusBadge status={status} />
      </td>

      <td className={styles.actionCell}>
        <button
          type="button"
          className={styles.undoButton}
          onClick={() => onUndo(original.sno)}
          disabled={!isModified || saving}
        >
          Undo row
        </button>
      </td>
    </tr>
  );
});

/* ------------------------------------------------------------------ *
 *  Review drawer — a bottom sheet, never a modal
 * ------------------------------------------------------------------ */

const ReviewDrawer = React.memo(function ReviewDrawer({
  open,
  cards,
  rowCount,
  fieldCount,
  saving,
  onCollapse,
  onDiscard,
  onSave,
}) {
  return (
    <div
      className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`}
      role="region"
      aria-label="Review pending changes"
      aria-hidden={!open}
    >
      <div className={styles.drawerHeader}>
        <h2 className={styles.drawerTitle}>Review changes</h2>
        <button type="button" className={styles.drawerClose} onClick={onCollapse}>
          Collapse
        </button>
      </div>

      <div className={styles.drawerBody}>
        {cards.map((card) => (
          <div key={card.sno} className={styles.reviewCard}>
            <div className={styles.reviewCardHead}>
              <div>
                <span className={styles.reviewLabel}>Barcode</span>
                <span className={styles.reviewValue}>{card.barcode || '—'}</span>
              </div>
              <div>
                <span className={styles.reviewLabel}>Category</span>
                <span className={styles.reviewValue}>{card.category || '—'}</span>
              </div>
            </div>

            <div className={styles.reviewGrid}>
              <div className={`${styles.reviewGridHead} ${styles.reviewGridRow}`}>
                <span>Field</span>
                <span>Previous</span>
                <span aria-hidden="true" />
                <span>New</span>
              </div>
              {card.changes.map((change) => (
                <div key={change.field} className={styles.reviewGridRow}>
                  <span className={styles.reviewField}>{change.label}</span>
                  <span className={styles.previousBadge}>{change.previous || '—'}</span>
                  <span className={styles.reviewArrow} aria-hidden="true">
                    →
                  </span>
                  <span className={styles.newBadge}>{change.next || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.drawerFooter}>
        <div className={styles.drawerSummary}>
          <strong>{rowCount}</strong> {rowCount === 1 ? 'row' : 'rows'} ·{' '}
          <strong>{fieldCount}</strong> {fieldCount === 1 ? 'field' : 'fields'}
        </div>
        <div className={styles.drawerActions}>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={onDiscard}
            disabled={saving}
          >
            Discard all
          </button>
          <button type="button" className={styles.ghostButton} onClick={onCollapse}>
            Collapse
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ *
 *  Main component
 * ------------------------------------------------------------------ */

const ClothList = () => {
  const navigate = useNavigate();

  // originalData is the untouched server truth — never mutated after load.
  const [originalData, setOriginalData] = useState([]);
  // editedData holds a draft per row, keyed by sno.
  const [editedData, setEditedData] = useState({});
  // Per-row save status: 'saved' | 'failed'. Absent means clean/modified.
  const [rowStatus, setRowStatus] = useState({});

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);

  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const toastTimer = useRef(null);
  const fileInputRef = useRef(null);

  const showToast = useCallback((type, text) => {
    setToast({ type, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  /* ----------------------------- Loading ----------------------------- */

  const loadItems = useCallback(async () => {
    const storeId = getStoreId();
    if (!storeId) {
      setLoading(false);
      setLoadError('No store found on this session. Sign in again to load cloth items.');
      return;
    }

    setLoading(true);
    setLoadError(null);
    const response = await fetchClothListByStore(storeId);

    if (succeeded(response)) {
      const rows = unwrap(response);
      setOriginalData(rows);
      setEditedData({});
      setRowStatus({});
    } else {
      setLoadError(response?.message || 'Could not load cloth items. Try again.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  /* --------------------------- Editing ------------------------------ */

  // The live draft for a row: its edited version if present, else the original slice.
  const draftFor = useCallback(
    (row) => editedData[row.sno] ?? toDraft(row),
    [editedData]
  );

  const handleChange = useCallback(
    (sno, field, value) => {
      setEditedData((prev) => {
        const original = originalData.find((row) => row.sno === sno);
        if (!original) return prev;

        const current = prev[sno] ?? toDraft(original);
        const next = { ...current, [field]: value };

        // Switching cloth type instantly reconciles panna type.
        if (field === 'type') {
          next.pannaType = resolvePannaType(value, current.pannaType);
        }

        // If the row now matches the original in every field, drop the draft
        // so the row reverts to a normal, unmodified state automatically.
        if (diffFields(original, next).length === 0) {
          const { [sno]: _removed, ...rest } = prev;
          return rest;
        }

        return { ...prev, [sno]: next };
      });

      // Any edit clears a stale saved/failed badge on that row.
      setRowStatus((prev) => {
        if (!prev[sno]) return prev;
        const { [sno]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [originalData]
  );

  const handleUndoRow = useCallback((sno) => {
    setEditedData((prev) => {
      const { [sno]: _removed, ...rest } = prev;
      return rest;
    });
    setRowStatus((prev) => {
      const { [sno]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleDiscardAll = useCallback(() => {
    setEditedData({});
    setRowStatus({});
    setDrawerOpen(false);
  }, []);

  /* --------------------- Derived change tracking -------------------- */

  // For every dirty row: its diff fields, validation errors, and status.
  const modifiedIndex = useMemo(() => {
    const map = new Map();
    for (const sno of Object.keys(editedData)) {
      const original = originalData.find((row) => String(row.sno) === String(sno));
      if (!original) continue;
      const draft = editedData[sno];
      const fields = diffFields(original, draft);
      if (fields.length === 0) continue;
      map.set(original.sno, {
        original,
        draft,
        fields,
        errors: validateDraft(draft),
      });
    }
    return map;
  }, [editedData, originalData]);

  const modifiedCount = modifiedIndex.size;

  const changedFieldCount = useMemo(() => {
    let total = 0;
    for (const entry of modifiedIndex.values()) total += entry.fields.length;
    return total;
  }, [modifiedIndex]);

  const hasErrors = useMemo(() => {
    for (const entry of modifiedIndex.values()) {
      if (Object.keys(entry.errors).length > 0) return true;
    }
    return false;
  }, [modifiedIndex]);

  const canSave = modifiedCount > 0 && !hasErrors && !saving;

  /* ------------------------------ Search ----------------------------- */

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return originalData;
    return originalData.filter((row) =>
      [
        row.itemBarcodeID,
        row.itemCategory,
        row.description,
        row.type,
        row.pannaType,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term))
    );
  }, [originalData, search]);

  /* --------------------------- Review cards -------------------------- */

  const reviewCards = useMemo(() => {
    const cards = [];
    for (const entry of modifiedIndex.values()) {
      const { original, draft, fields } = entry;
      cards.push({
        sno: original.sno,
        barcode: original.itemBarcodeID,
        category: original.itemCategory,
        changes: fields.map((field) => ({
          field,
          label: FIELD_LABELS[field],
          previous: String(original[field] ?? ''),
          next: String(draft[field] ?? ''),
        })),
      });
    }
    return cards;
  }, [modifiedIndex]);

  /* ------------------------------ Export ----------------------------- */

  // Export reflects the current view (respects search) with pending edits applied,
  // so what you export is what you see. Barcode + editable fields only.
  const handleExport = useCallback(() => {
    if (filtered.length === 0) {
      showToast('error', 'Nothing to export in the current view.');
      return;
    }

    const rows = filtered.map((row) => {
      const draft = editedData[row.sno] ?? toDraft(row);
      return {
        itemBarcodeID: row.itemBarcodeID ?? '',
        itemCategory: draft.itemCategory ?? '',
        type: draft.type ?? '',
        pannaType: draft.pannaType ?? '',
        price: draft.price ?? '',
        wholeSalePrice: draft.wholeSalePrice ?? '',
      };
    });

    const csv = rowsToCsv(rows);
    const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);

    const link = document.createElement('a');
    link.href = url;
    link.download = `cloth-items-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast('success', `Exported ${rows.length} ${rows.length === 1 ? 'row' : 'rows'}.`);
  }, [filtered, editedData, showToast]);

  /* ------------------------------ Import ----------------------------- */

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  // Import flow: parse CSV → for each barcode, fetch the current record by barcodeId,
  // then stage the imported values as pending edits against that record. Nothing is
  // saved automatically — changes land in the review drawer for the user to confirm.
  const handleImportFile = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const storeId = getStoreId();
      if (!storeId) {
        showToast('error', 'No store on this session. Sign in again to import.');
        return;
      }

      let text = '';
      try {
        text = await file.text();
      } catch {
        showToast('error', 'Could not read that file.');
        return;
      }

      const records = parseCsv(text);
      if (records.length === 0) {
        showToast('error', 'No rows found in that file.');
        return;
      }

      // Must at least carry a barcode column to match against.
      const withBarcode = records.filter((r) =>
        String(r.itemBarcodeID ?? '').trim() !== ''
      );
      if (withBarcode.length === 0) {
        showToast('error', 'File is missing an "itemBarcodeID" column.');
        return;
      }

      setImporting(true);

      // Index existing rows by barcode for quick local matching.
      const byBarcode = new Map(
        originalData.map((row) => [String(row.itemBarcodeID), row])
      );

      let matched = 0;
      let fetched = 0;
      let skipped = 0;
      const newOriginals = [];
      const stagedEdits = {};

      for (const record of withBarcode) {
        const barcode = String(record.itemBarcodeID).trim();
        let target = byBarcode.get(barcode);

        // Not already loaded? Fetch it by barcode so we can diff against truth.
        if (!target) {
          try {
            const response = await findClothById(barcode, storeId);
            if (succeeded(response)) {
              const found = response?.payload ?? unwrap(response)[0];
              if (found) {
                target = found;
                byBarcode.set(barcode, found);
                newOriginals.push(found);
                fetched += 1;
              }
            }
          } catch {
            /* fall through to skip */
          }
        }

        if (!target) {
          skipped += 1;
          continue;
        }

        const imported = normaliseImported(record);
        // Only stage fields that actually differ from the current record.
        if (diffFields(target, imported).length > 0) {
          stagedEdits[target.sno] = imported;
          matched += 1;
        }
      }

      // Fold any freshly fetched rows into the table so they render.
      if (newOriginals.length > 0) {
        setOriginalData((prev) => {
          const seen = new Set(prev.map((r) => String(r.sno)));
          const additions = newOriginals.filter((r) => !seen.has(String(r.sno)));
          return additions.length > 0 ? [...prev, ...additions] : prev;
        });
      }

      // Merge staged edits into the existing draft map.
      setEditedData((prev) => ({ ...prev, ...stagedEdits }));
      setRowStatus((prev) => {
        const next = { ...prev };
        for (const sno of Object.keys(stagedEdits)) delete next[sno];
        return next;
      });

      setImporting(false);

      if (matched === 0) {
        showToast(
          'error',
          skipped > 0
            ? `No changes staged. ${skipped} ${skipped === 1 ? 'barcode' : 'barcodes'} not found.`
            : 'Imported rows already match the current data.'
        );
      } else {
        const parts = [`Staged ${matched} ${matched === 1 ? 'row' : 'rows'} for review`];
        if (fetched > 0) parts.push(`${fetched} fetched by barcode`);
        if (skipped > 0) parts.push(`${skipped} not found`);
        showToast('success', `${parts.join(' · ')}. Review and save.`);
        setDrawerOpen(true);
      }
    },
    [originalData, showToast]
  );

  /* ------------------------------ Saving ----------------------------- */

  const handleSaveAll = useCallback(async () => {
    if (!canSave) return;

    setSaving(true);
    setRowStatus((prev) => {
      const next = { ...prev };
      for (const sno of modifiedIndex.keys()) next[sno] = 'saving';
      return next;
    });

    const entries = Array.from(modifiedIndex.values());

    // Fire every row update in parallel; capture per-row outcome without throwing.
    const results = await Promise.all(
      entries.map(async (entry) => {
        const requests = buildUpdateRequests(entry.original, entry.draft);
        try {
          const response = await updateClothItem(entry.original.sno, requests);
          const ok = String(response?.status).toLowerCase() === 'success';
          return { sno: entry.original.sno, ok, payload: response?.payload };
        } catch {
          return { sno: entry.original.sno, ok: false };
        }
      })
    );

    const succeededRows = results.filter((r) => r.ok);
    const failedRows = results.filter((r) => !r.ok);

    // Merge saved payloads into originalData and drop those drafts.
    if (succeededRows.length > 0) {
      const savedById = new Map(succeededRows.map((r) => [r.sno, r.payload]));
      setOriginalData((prev) =>
        prev.map((row) =>
          savedById.has(row.sno)
            ? { ...row, ...(savedById.get(row.sno) || {}) }
            : row
        )
      );
    }

    setEditedData((prev) => {
      const next = { ...prev };
      for (const r of succeededRows) delete next[r.sno];
      return next;
    });

    setRowStatus(() => {
      const next = {};
      for (const r of failedRows) next[r.sno] = 'failed';
      for (const r of succeededRows) next[r.sno] = 'saved';
      return next;
    });

    setSaving(false);

    if (failedRows.length === 0) {
      setDrawerOpen(false);
      showToast('success', `Saved ${succeededRows.length} ${succeededRows.length === 1 ? 'row' : 'rows'}.`);
    } else if (succeededRows.length === 0) {
      showToast('error', `Couldn't save ${failedRows.length} ${failedRows.length === 1 ? 'row' : 'rows'}. Retry when ready.`);
    } else {
      showToast(
        'error',
        `Saved ${succeededRows.length}, but ${failedRows.length} failed. Retry the highlighted ${failedRows.length === 1 ? 'row' : 'rows'}.`
      );
    }
  }, [canSave, modifiedIndex, showToast]);

  /* ------------------------------ Render ----------------------------- */

  const showPanel = modifiedCount > 0;
  const busy = loading || saving || importing;

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate('/admin/dashboard')}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>
            <h1 className={styles.title}>Cloth Management</h1>
          </div>
          <div className={styles.headerMeta}>
            <span className={styles.metaChip}>
              <strong>{modifiedCount}</strong> rows modified
            </span>
            <span className={styles.metaChip}>
              <strong>{changedFieldCount}</strong> fields changed
            </span>
          </div>
        </div>

        <div className={styles.toolbar}>
          <input
            className={styles.search}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search barcode, category, description, type or panna"
          />

          {/* Hidden file input drives Import */}
          <input
            ref={fileInputRef}
            className={styles.fileInput}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportFile}
          />

          <button
            type="button"
            className={styles.navyButton}
            onClick={handleExport}
            disabled={busy}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Export
          </button>
          <button
            type="button"
            className={styles.navyButton}
            onClick={openFilePicker}
            disabled={busy}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 21V9m0 0l-4 4m4-4l4 4M5 3h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {importing ? 'Importing…' : 'Import'}
          </button>

          <button
            type="button"
            className={styles.ghostButton}
            onClick={loadItems}
            disabled={busy}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={handleDiscardAll}
            disabled={!showPanel || saving}
          >
            Discard all
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSaveAll}
            disabled={!canSave}
          >
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>
      </header>

      {/* Body */}
      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.placeholder}>
            <span className={styles.spinner} aria-hidden="true" />
            <p>Loading cloth items…</p>
          </div>
        ) : loadError ? (
          <div className={styles.placeholder}>
            <p className={styles.errorText}>{loadError}</p>
            <button type="button" className={styles.primaryButton} onClick={loadItems}>
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.placeholder}>
            <div className={styles.emptyArt} aria-hidden="true">
              <svg viewBox="0 0 64 64" width="72" height="72" fill="none">
                <rect x="8" y="14" width="48" height="36" rx="4" stroke="currentColor" strokeWidth="2.5" />
                <path d="M8 24h48" stroke="currentColor" strokeWidth="2.5" />
                <path d="M20 34h24M20 42h16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <p className={styles.emptyTitle}>No cloth items found</p>
            <p className={styles.emptyHint}>
              {originalData.length === 0
                ? 'Add one from the Add Cloth Item page to get started.'
                : 'No items match your search. Clear it to see everything.'}
            </p>
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thBarcode}>Barcode</th>
                  <th>Item Category</th>
                  <th>Cloth Type</th>
                  <th>Panna Type</th>
                  <th>Retail Price</th>
                  <th>Wholesale Price</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th className={styles.thAction}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => {
                  const entry = modifiedIndex.get(row.sno);
                  const status = rowStatus[row.sno]
                    || (entry ? 'modified' : 'clean');
                  return (
                    <ClothRow
                      key={row.sno}
                      index={index}
                      original={row}
                      draft={draftFor(row)}
                      changedFields={entry ? entry.fields : []}
                      errors={entry ? entry.errors : {}}
                      status={status}
                      onChange={handleChange}
                      onUndo={handleUndoRow}
                      saving={saving}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Saving / importing overlay */}
        {(saving || importing) && (
          <div className={styles.savingOverlay} aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            <p>{importing ? 'Importing file…' : 'Saving changes…'}</p>
          </div>
        )}
      </div>

      {/* Sticky pending-changes panel */}
      <div
        className={`${styles.pendingPanel} ${showPanel ? styles.pendingPanelOpen : ''}`}
        aria-hidden={!showPanel}
      >
        <div className={styles.pendingInfo}>
          <span className={styles.pendingTitle}>Pending changes</span>
          <span className={styles.pendingCounts}>
            <strong>{modifiedCount}</strong> {modifiedCount === 1 ? 'row' : 'rows'} ·{' '}
            <strong>{changedFieldCount}</strong> {changedFieldCount === 1 ? 'field' : 'fields'}
            {hasErrors && <span className={styles.pendingWarn}> · fix invalid prices to save</span>}
          </span>
        </div>
        <div className={styles.pendingActions}>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={handleDiscardAll}
            disabled={saving}
          >
            Discard all
          </button>
          <button
            type="button"
            className={styles.ghostButton}
            onClick={() => setDrawerOpen((open) => !open)}
          >
            {drawerOpen ? 'Hide review' : 'Review changes'}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSaveAll}
            disabled={!canSave}
          >
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>
      </div>

      {/* Backdrop dims the table behind the drawer (click to collapse) */}
      <div
        className={`${styles.drawerBackdrop} ${drawerOpen && showPanel ? styles.drawerBackdropOpen : ''}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      <ReviewDrawer
        open={drawerOpen && showPanel}
        cards={reviewCards}
        rowCount={modifiedCount}
        fieldCount={changedFieldCount}
        saving={saving}
        onCollapse={() => setDrawerOpen(false)}
        onDiscard={handleDiscardAll}
        onSave={handleSaveAll}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="status"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default ClothList;